import { z } from 'zod/v4';
import {
  PERSON_NAME_MAX_LENGTH,
  validatePersonName,
} from '@/features/teams/lib/person-name';

const PASSWORD_MIN_LENGTH = 8;

/** Mirrors server `passwordPolicySchema` (auth.schema.ts OQ-6). */
export const passwordPolicySchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .regex(/[A-Z]/, 'Password must include an uppercase letter')
  .regex(/[a-z]/, 'Password must include a lowercase letter')
  .regex(/[0-9]/, 'Password must include a digit');

function phoneField(label: string) {
  return z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(50, `${label} is too long`)
    .refine((value) => value.replace(/\D/g, '').length >= 10, {
      message: `${label} must include at least 10 digits`,
    });
}

/**
 * DEF-283 — reuse Teams `validatePersonName` (includes `\p{N}`) so register
 * accepts digits the same way BE `personNameSchema` does.
 */
function personNameField(label: string) {
  return z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(PERSON_NAME_MAX_LENGTH, `${label} is too long`)
    .superRefine((value, ctx) => {
      const message = validatePersonName(value);
      if (message) {
        ctx.addIssue({ code: 'custom', message });
      }
    });
}

/** Step 1 — invite email gate. */
export const registerEmailSchema = z.object({
  email: z.email('Please enter a valid email address'),
});

/** Step 2 — profile + password before OTP. */
export const registerProfileSchema = z.object({
  firstName: personNameField('First name'),
  lastName: personNameField('Last name'),
  companyName: z
    .string()
    .trim()
    .min(1, 'Company is required')
    .max(255, 'Company name is too long'),
  workPhone: phoneField('Work phone'),
  authPhone: phoneField('Mobile number'),
  password: passwordPolicySchema,
  termsAccepted: z.boolean().refine((value) => value === true, {
    message: 'You must accept the Terms of Service and Privacy Policy',
  }),
});

export type RegisterEmailForm = z.infer<typeof registerEmailSchema>;
export type RegisterProfileForm = z.infer<typeof registerProfileSchema>;
